import { Injectable, CanActivate, ExecutionContext, Inject, Optional } from '@nestjs/common';
import { HttpAdapterHost, Reflector } from '@nestjs/core';

import { RATE_LIMIT_SERVICE, RATE_LIMIT_PLUGIN_OPTIONS } from '../../../shared/constants';
import { RateLimitExceededError } from '../../../shared/errors';
import { IRateLimitPluginOptions, IRateLimitResult } from '../../../shared/types';
import { IRateLimitService } from '../../application/ports/rate-limit-service.port';
import { RATE_LIMIT_OPTIONS, IRateLimitOptions } from '../decorators/rate-limit.decorator';

// Optional metrics integration
const METRICS_SERVICE = Symbol.for('METRICS_SERVICE');

interface IMetricsService {
  incrementCounter(name: string, labels?: Record<string, string>, value?: number): void;
}

// Optional tracing integration
const TRACING_SERVICE = Symbol.for('TRACING_SERVICE');

interface ISpan {
  setAttribute(key: string, value: unknown): this;
  addEvent(name: string, attributes?: Record<string, unknown>): this;
  setStatus(status: 'OK' | 'ERROR'): this;
  recordException(error: Error): this;
  end(): void;
}

interface ITracingService {
  startSpan(name: string, options?: { kind?: string; attributes?: Record<string, unknown> }): ISpan;
}

/**
 * Per-request marker that records the rate limit has already been consumed for
 * the current request.
 *
 * The {@link RateLimit} decorator binds this guard via `UseGuards`. NestJS does
 * not deduplicate the same guard across binding scopes, so a route decorated on
 * both the class and the method — or a route decorated with `@RateLimit` while
 * the guard is also registered globally as `APP_GUARD` — would otherwise run
 * `canActivate` (and consume a point) more than once for a single request. The
 * marker makes consumption idempotent within one request.
 */
const RATE_LIMIT_CONSUMED = Symbol('redisx.rateLimitConsumed');

/**
 * Rate limit guard.
 * Enforces rate limiting based on @RateLimit() decorator configuration.
 */
@Injectable()
export class RateLimitGuard implements CanActivate {
  constructor(
    @Inject(RATE_LIMIT_SERVICE)
    private readonly rateLimitService: IRateLimitService,
    @Inject(RATE_LIMIT_PLUGIN_OPTIONS)
    private readonly config: IRateLimitPluginOptions,
    @Inject(Reflector) private readonly reflector: Reflector,
    @Inject(HttpAdapterHost) private readonly adapterHost: HttpAdapterHost,
    @Optional() @Inject(METRICS_SERVICE) private readonly metrics?: IMetricsService,
    @Optional() @Inject(TRACING_SERVICE) private readonly tracing?: ITracingService,
  ) {}

  /**
   * Guard activation logic.
   * Checks rate limit and sets response headers.
   */
  async canActivate(context: ExecutionContext): Promise<boolean> {
    // The limit must be consumed at most once per request even if the guard is
    // bound more than once (class + method decorator, or decorator + global
    // APP_GUARD). If a prior binding already consumed it, allow without
    // re-consuming.
    const request = context.switchToHttp().getRequest<Record<symbol, unknown>>();
    if (request[RATE_LIMIT_CONSUMED]) {
      return true;
    }

    const options = this.getOptions(context);

    // Check skip condition
    if (await this.shouldSkip(context, options)) {
      return true;
    }

    request[RATE_LIMIT_CONSUMED] = true;

    const key = await this.extractKey(context, options);
    const span = this.tracing?.startSpan('ratelimit.check', {
      kind: 'INTERNAL',
      attributes: { 'ratelimit.key': key },
    });

    try {
      const result = await this.rateLimitService.check(key, options);

      // Set response headers
      this.setHeaders(context, result);

      span?.setAttribute('ratelimit.allowed', result.allowed);
      span?.setAttribute('ratelimit.remaining', result.remaining);
      span?.setAttribute('ratelimit.limit', result.limit);

      if (!result.allowed) {
        this.metrics?.incrementCounter('redisx_ratelimit_requests_total', { status: 'rejected' });
        span?.setStatus('OK'); // Not an error - rate limit working as expected
        throw this.createError(result, options);
      }

      this.metrics?.incrementCounter('redisx_ratelimit_requests_total', { status: 'allowed' });
      span?.setStatus('OK');
      return true;
    } catch (error) {
      if (!(error instanceof RateLimitExceededError)) {
        span?.recordException(error as Error);
        span?.setStatus('ERROR');
      }
      throw error;
    } finally {
      span?.end();
    }
  }

  /**
   * Get rate limit options from decorator metadata.
   * Merges class-level and method-level options.
   */
  private getOptions(context: ExecutionContext): IRateLimitOptions {
    const handlerOptions = this.reflector.get<IRateLimitOptions>(RATE_LIMIT_OPTIONS, context.getHandler());
    const classOptions = this.reflector.get<IRateLimitOptions>(RATE_LIMIT_OPTIONS, context.getClass());

    return { ...classOptions, ...handlerOptions };
  }

  /**
   * Extract rate limit key from context.
   */
  private async extractKey(context: ExecutionContext, options: IRateLimitOptions): Promise<string> {
    const extractor = options.key ?? this.config.defaultKeyExtractor ?? 'ip';

    // Custom function
    if (typeof extractor === 'function') {
      return await extractor(context);
    }

    // String key
    if (typeof extractor === 'string' && !['ip', 'user', 'apiKey'].includes(extractor)) {
      return extractor;
    }

    // Predefined extractors
    const request = context.switchToHttp().getRequest();

    switch (extractor) {
      case 'ip':
        return this.getClientIp(request);
      case 'user':
        return this.getUserId(request);
      case 'apiKey':
        return this.getApiKey(request);
      default:
        return this.getClientIp(request);
    }
  }

  /**
   * Get client IP address.
   */
  private getClientIp(request: Request & { ip?: string; ips?: string[] }): string {
    // Secure default: take the IP from the framework. `request.ip` respects the
    // app's own trust-proxy configuration (Express `trust proxy` / Fastify
    // `trustProxy`), so it is the real client IP and cannot be spoofed by a
    // client-supplied header. Only read forwarding headers ourselves when the
    // operator explicitly opts in via `trustProxy: true`.
    if (this.config.trustProxy === true) {
      const forwardedFor = (request.headers as unknown as Record<string, string>)['x-forwarded-for'];
      if (forwardedFor) {
        const ips = forwardedFor.split(',').map((ip) => ip.trim());
        if (ips[0]) return ips[0];
      }

      const realIp = (request.headers as unknown as Record<string, string>)['x-real-ip'];
      if (realIp) {
        return realIp;
      }
    }

    return request.ip || 'unknown';
  }

  /**
   * Get user ID from request.
   */
  private getUserId(request: Request & { user?: { id?: string } }): string {
    const userId = request.user?.id;
    if (!userId) {
      throw new Error('User ID not found. Ensure authentication guard runs before rate limit guard.');
    }
    return `user:${userId}`;
  }

  /**
   * Get API key from request.
   */
  private getApiKey(request: Request): string {
    const apiKey = (request.headers as unknown as Record<string, string>)['x-api-key'] || (request.headers as unknown as Record<string, string>)['authorization'];

    if (!apiKey) {
      throw new Error('API key not found. Ensure request includes X-API-Key or Authorization header.');
    }

    return `apikey:${apiKey}`;
  }

  /**
   * Set response headers.
   */
  private setHeaders(context: ExecutionContext, result: IRateLimitResult): void {
    if (this.config.includeHeaders === false) {
      return;
    }

    const httpAdapter = this.adapterHost.httpAdapter;
    if (!httpAdapter) {
      throw new Error('RateLimitGuard: HttpAdapterHost is not initialized. Ensure the NestJS application has bootstrapped with an HTTP adapter before handling requests.');
    }

    const response = context.switchToHttp().getResponse();
    const headers = this.config.headers ?? {};

    const limitHeader = headers.limit ?? 'X-RateLimit-Limit';
    const remainingHeader = headers.remaining ?? 'X-RateLimit-Remaining';
    const resetHeader = headers.reset ?? 'X-RateLimit-Reset';
    const retryAfterHeader = headers.retryAfter ?? 'Retry-After';

    httpAdapter.setHeader(response, limitHeader, result.limit.toString());
    httpAdapter.setHeader(response, remainingHeader, result.remaining.toString());
    httpAdapter.setHeader(response, resetHeader, result.reset.toString());

    if (!result.allowed && result.retryAfter) {
      httpAdapter.setHeader(response, retryAfterHeader, result.retryAfter.toString());
    }
  }

  /**
   * Create error when rate limit exceeded.
   */
  private createError(result: IRateLimitResult, options: IRateLimitOptions): Error {
    // Use custom error factory if provided
    if (options.errorFactory) {
      return options.errorFactory(result);
    }

    // Use module-level error factory if provided
    if (this.config.errorFactory) {
      return this.config.errorFactory(result);
    }

    // Default error
    const message = options.message ?? `Rate limit exceeded. Try again in ${result.retryAfter || 0} seconds.`;

    return new RateLimitExceededError(message, result);
  }

  /**
   * Check if rate limiting should be skipped.
   */
  private async shouldSkip(context: ExecutionContext, options: IRateLimitOptions): Promise<boolean> {
    // Check decorator-level skip
    if (options.skip) {
      return await options.skip(context);
    }

    // Check module-level skip
    if (this.config.skip) {
      return await this.config.skip(context);
    }

    return false;
  }
}
